/**
 * Audio Manager for simple Web Audio API beep/boop sound effects.
 *
 * Provides placeholder sounds using synthesized waveforms until
 * real audio assets are added.
 */
export class AudioManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  /**
   * Get or create the AudioContext.
   * Lazy initialization to avoid issues before user interaction.
   */
  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  /**
   * Play a simple beep tone.
   *
   * @param frequency - Frequency in Hz (default: 440Hz = A4)
   * @param duration - Duration in seconds (default: 0.1s)
   * @param volume - Volume multiplier 0-1 (default: 0.3)
   * @param type - Oscillator type (default: 'square' for retro feel)
   */
  playBeep(
    frequency: number = 440,
    duration: number = 0.1,
    volume: number = 0.3,
    type: OscillatorType = 'square'
  ): void {
    if (!this.enabled) return;

    try {
      const ctx = this.getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = frequency;
      osc.type = type;

      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch (error) {
      console.warn('Failed to play audio:', error);
    }
  }

  /**
   * Play a two-note sequence.
   *
   * @param freq1 - First frequency in Hz
   * @param freq2 - Second frequency in Hz
   * @param duration - Duration of each note in seconds
   * @param gap - Gap between notes in seconds
   * @param volume - Volume multiplier 0-1
   */
  private playDualBeep(
    freq1: number,
    freq2: number,
    duration: number = 0.1,
    gap: number = 0.05,
    volume: number = 0.3
  ): void {
    this.playBeep(freq1, duration, volume);
    setTimeout(() => {
      this.playBeep(freq2, duration, volume);
    }, (duration + gap) * 1000);
  }

  /**
   * Play an arpeggio (rising sequence of notes).
   *
   * @param baseFreq - Base frequency in Hz
   * @param notes - Number of notes in the arpeggio
   * @param interval - Interval multiplier (e.g., 1.5 for perfect fifth)
   * @param noteDuration - Duration of each note in seconds
   * @param volume - Volume multiplier 0-1
   */
  private playArpeggio(
    baseFreq: number,
    notes: number = 3,
    interval: number = 1.5,
    noteDuration: number = 0.08,
    volume: number = 0.3
  ): void {
    let freq = baseFreq;
    for (let i = 0; i < notes; i++) {
      setTimeout(() => {
        this.playBeep(freq, noteDuration, volume);
      }, i * noteDuration * 1000);
      freq *= interval;
    }
  }

  /**
   * Play a descending sequence of notes.
   *
   * @param baseFreq - Starting frequency in Hz
   * @param notes - Number of notes
   * @param interval - Interval divisor
   * @param noteDuration - Duration of each note in seconds
   * @param volume - Volume multiplier 0-1
   */
  private playDescending(
    baseFreq: number,
    notes: number = 4,
    interval: number = 1.2,
    noteDuration: number = 0.15,
    volume: number = 0.4
  ): void {
    let freq = baseFreq;
    for (let i = 0; i < notes; i++) {
      setTimeout(() => {
        this.playBeep(freq, noteDuration, volume);
      }, i * noteDuration * 1000);
      freq /= interval;
    }
  }

  // ========== PREDEFINED GAME SOUNDS ==========

  /**
   * Player fires weapon - quick high-pitched beep.
   */
  shoot(): void {
    this.playBeep(600, 0.05, 0.2);
  }

  /**
   * Projectile hits target - mid-range thud.
   */
  hit(): void {
    this.playBeep(200, 0.1, 0.3);
  }

  /**
   * Player picks up part - dual ascending beep.
   */
  pickup(): void {
    this.playDualBeep(800, 1000, 0.08, 0.02, 0.25);
  }

  /**
   * Player dashes - whoosh sound.
   */
  dash(): void {
    this.playBeep(300, 0.15, 0.2, 'triangle');
  }

  /**
   * Enemy dies - low descending tone.
   */
  enemyDie(): void {
    this.playBeep(150, 0.2, 0.35, 'sawtooth');
  }

  /**
   * Player takes damage - harsh low tone.
   */
  playerHit(): void {
    this.playBeep(100, 0.15, 0.45, 'sawtooth');
  }

  /**
   * Part fusion successful - rising arpeggio.
   */
  fusion(): void {
    this.playArpeggio(400, 4, 1.5, 0.08, 0.3);
  }

  /**
   * Boss phase transition - low rumbling tone.
   */
  bossPhase(): void {
    this.playBeep(80, 0.3, 0.4, 'sawtooth');
  }

  /**
   * Game over - descending notes.
   */
  gameOver(): void {
    this.playDescending(440, 5, 1.2, 0.2, 0.4);
  }

  /**
   * Door unlocks (room cleared) - dual ascending beep.
   */
  doorUnlock(): void {
    this.playDualBeep(500, 700, 0.1, 0.05, 0.3);
  }

  /**
   * Boss defeated - triumphant arpeggio.
   */
  bossDefeated(): void {
    this.playArpeggio(300, 5, 1.6, 0.1, 0.35);
  }

  /**
   * Floor transition - short ascending scale.
   */
  floorTransition(): void {
    this.playArpeggio(350, 3, 1.4, 0.08, 0.25);
  }

  /**
   * Enable or disable audio.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if audio is enabled.
   */
  isEnabled(): boolean {
    return this.enabled;
  }
}

// Export singleton instance
export const audio = new AudioManager();
